import WebSocket from 'ws';
import { CDP_CONNECT_TIMEOUT_MS, CDP_POOL_MESSAGE_ID_START, CDP_TIMEOUT_MS } from '../constants';
import { logger } from './logger';
/**
 * Singleton pool of long-lived CDP WebSocket connections.
 *
 * Why this exists:
 * - The previous `electron-connection.ts` opened a fresh WebSocket per call
 *   (~50 connections / 5 seconds for `wait_*` polling). That is unsustainable
 *   and incompatible with `MutationObserver` based primitives that need a
 *   long-lived connection for streaming events.
 * - This pool keeps one WebSocket per CDP target, multiplexes requests using
 *   monotonic message IDs, caches `Runtime.enable` per connection, and routes
 *   responses back to the right caller through a pendingMessages map.
 *
 * Reconnection policy: callers retry on failure. The pool removes a broken
 * connection from its cache so the *next* `getConnection` opens a new socket.
 */
export class CdpConnectionPool {
    static instance = null;
    /**
     * Storing `Promise<PooledConnection>` (not `PooledConnection`) prevents the
     * race where two concurrent callers both see the cache miss and each open a
     * WebSocket. The first caller writes the in-flight Promise; the second
     * caller awaits the same Promise.
     */
    connections = new Map();
    static getInstance() {
        if (!CdpConnectionPool.instance) {
            CdpConnectionPool.instance = new CdpConnectionPool();
        }
        return CdpConnectionPool.instance;
    }
    /**
     * Reset the singleton. ONLY used by tests — production code never calls this.
     * @internal
     */
    static resetForTesting() {
        CdpConnectionPool.instance = null;
    }
    /**
     * Get (or create) the pooled connection for a target.
     * Concurrent calls for the same target share the same in-flight Promise.
     */
    async getConnection(target) {
        if (!target.webSocketDebuggerUrl) {
            throw new Error('Target has no webSocketDebuggerUrl');
        }
        const cached = this.connections.get(target.id);
        if (cached)
            return cached;
        const opening = this.openConnection(target);
        this.connections.set(target.id, opening);
        try {
            return await opening;
        }
        catch (error) {
            // Eviction: failed Promise must not stick in the cache.
            this.connections.delete(target.id);
            throw error;
        }
    }
    /**
     * Send any CDP method on the pooled connection.
     * @returns The CDP `result` payload (caller decides shape).
     * @example
     * pool.send(target, 'Input.dispatchMouseEvent', { type: 'mousePressed', x, y })
     */
    async send(target, method, params = {}, options = {}) {
        const conn = await this.getConnection(target);
        return this.sendOnConnection(conn, method, params, options);
    }
    /**
     * Convenience wrapper around `Runtime.evaluate` with sensible defaults
     * (`returnByValue: true`, `awaitPromise: false`).
     */
    async evaluate(target, expression, options = {}) {
        const conn = await this.getConnection(target);
        await this.ensureDomainEnabled(conn, 'Runtime');
        const params = {
            expression,
            returnByValue: options.returnByValue ?? true,
            awaitPromise: options.awaitPromise ?? false,
        };
        if (options.userGesture)
            params.userGesture = true;
        return (await this.sendOnConnection(conn, 'Runtime.evaluate', params, {
            timeoutMs: options.timeoutMs,
        }));
    }
    /**
     * Ensure a CDP domain (`Runtime`, `Console`, `Page`, ...) is enabled on the
     * connection. Idempotent: subsequent calls for the same domain are no-ops.
     */
    async ensureDomainEnabled(conn, domain) {
        if (conn.enabledDomains.has(domain))
            return;
        await this.sendOnConnection(conn, `${domain}.enable`, {}, {});
        conn.enabledDomains.add(domain);
    }
    /** Close a single connection (e.g. after the underlying target disappears). */
    async close(targetId) {
        const cached = this.connections.get(targetId);
        if (!cached)
            return;
        this.connections.delete(targetId);
        try {
            const conn = await cached;
            this.terminateConnection(conn, new Error('Connection closed by pool'));
        }
        catch {
            // open failed — nothing to terminate
        }
    }
    /** Close every pooled connection. Used on shutdown / test teardown. */
    async closeAll() {
        const ids = Array.from(this.connections.keys());
        await Promise.all(ids.map((id) => this.close(id)));
    }
    // ------------------------------------------------------------------
    // private
    // ------------------------------------------------------------------
    openConnection(target) {
        return new Promise((resolve, reject) => {
            const ws = new WebSocket(target.webSocketDebuggerUrl);
            const conn = {
                ws,
                target,
                pendingMessages: new Map(),
                nextMessageId: CDP_POOL_MESSAGE_ID_START,
                enabledDomains: new Set(),
                isClosing: false,
            };
            const connectTimeout = setTimeout(() => {
                ws.close();
                reject(new Error(`CDP connect timeout (${CDP_CONNECT_TIMEOUT_MS}ms): ${target.id}`));
            }, CDP_CONNECT_TIMEOUT_MS);
            ws.on('open', () => {
                clearTimeout(connectTimeout);
                logger.debug(`CDP pool: opened connection to ${target.title} (${target.id})`);
                resolve(conn);
            });
            ws.on('message', (data) => this.dispatchMessage(conn, data));
            ws.on('close', () => {
                this.handleConnectionClosed(conn, new Error('WebSocket closed'));
            });
            ws.on('error', (error) => {
                clearTimeout(connectTimeout);
                const wrapped = new Error(`WebSocket error: ${error.message}`);
                if (this.connections.get(target.id)) {
                    // Already open — surface to in-flight requests via close handler.
                    this.handleConnectionClosed(conn, wrapped);
                }
                reject(wrapped);
            });
        });
    }
    sendOnConnection(conn, method, params, options) {
        if (conn.isClosing || conn.ws.readyState !== WebSocket.OPEN) {
            return Promise.reject(new Error(`CDP connection unavailable for ${method}`));
        }
        const id = conn.nextMessageId++;
        const timeoutMs = options.timeoutMs ?? CDP_TIMEOUT_MS;
        return new Promise((resolve, reject) => {
            const timeoutHandle = setTimeout(() => {
                conn.pendingMessages.delete(id);
                reject(new Error(`CDP method timeout (${timeoutMs}ms): ${method}`));
            }, timeoutMs);
            conn.pendingMessages.set(id, { resolve, reject, timeoutHandle, method });
            try {
                conn.ws.send(JSON.stringify({ id, method, params }));
            }
            catch (sendError) {
                clearTimeout(timeoutHandle);
                conn.pendingMessages.delete(id);
                reject(sendError instanceof Error ? sendError : new Error(String(sendError)));
            }
        });
    }
    dispatchMessage(conn, data) {
        let response;
        try {
            response = JSON.parse(data.toString());
        }
        catch (error) {
            logger.warn('CDP pool: failed to parse message', error);
            return;
        }
        if (typeof response.id !== 'number') {
            // Event (e.g. Runtime.consoleAPICalled). Pool ignores; subscribers can
            // attach their own listener to the underlying ws if needed.
            return;
        }
        const pending = conn.pendingMessages.get(response.id);
        if (!pending)
            return;
        clearTimeout(pending.timeoutHandle);
        conn.pendingMessages.delete(response.id);
        if (response.error) {
            pending.reject(new Error(`CDP error (${pending.method}): ${response.error.message}`));
            return;
        }
        pending.resolve(response.result ?? null);
    }
    handleConnectionClosed(conn, reason) {
        if (conn.isClosing)
            return;
        conn.isClosing = true;
        this.connections.delete(conn.target.id);
        for (const pending of conn.pendingMessages.values()) {
            clearTimeout(pending.timeoutHandle);
            pending.reject(reason);
        }
        conn.pendingMessages.clear();
    }
    terminateConnection(conn, reason) {
        this.handleConnectionClosed(conn, reason);
        try {
            conn.ws.close();
        }
        catch {
            // best-effort
        }
    }
}
